import { isNativeCapacitorApp } from "@/integrations/clients/carAuction/nativeAuth";
import {
  hapticImpactSync,
  hapticNotificationSync,
} from "@/lib/native/capacitorBridge";

const CONFETTI_SOUND_SRC = "/assets/sound/confetti.mp3";
const OUTBID_SOUND_SRC = "/assets/sound/outbid.mp3";

/** Short attention haptic (native) + Vibration API (web when allowed). */
export function alertVibrate(pattern: number | number[] = [40, 60, 40]): void {
  if (isNativeCapacitorApp()) {
    hapticNotificationSync("warning");
    // Follow-up impact so it is felt even if notification haptic is muted
    window.setTimeout(() => hapticImpactSync("heavy"), 80);
    return;
  }
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate(pattern);
    }
  } catch {
    /* ignore */
  }
}

let audioCtx: AudioContext | null = null;

function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AC) return null;
  if (!audioCtx) audioCtx = new AC();
  return audioCtx;
}

function playTone(
  ctx: AudioContext,
  freq: number,
  start: number,
  dur: number,
  peak = 0.12,
  type: OscillatorType = "sine",
): void {
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0.0001, start);
  gain.gain.exponentialRampToValueAtTime(peak, start + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.start(start);
  osc.stop(start + dur + 0.02);
}

/** Active celebration clip — stopped when the overlay is dismissed. */
let confettiAudio: HTMLAudioElement | null = null;

/**
 * Play an mp3 from /public. Returns the Audio element if play() was started,
 * otherwise null. Browsers may still reject without a prior user gesture.
 */
function playMp3(src: string, volume = 0.75): HTMLAudioElement | null {
  try {
    if (typeof Audio === "undefined") return null;
    const audio = new Audio(src);
    audio.volume = volume;
    const playResult = audio.play();
    if (playResult && typeof playResult.catch === "function") {
      playResult.catch(() => {
        /* autoplay blocked — ignore */
      });
    }
    return audio;
  } catch {
    return null;
  }
}

/** Stop celebration sound immediately (e.g. when closing the overlay). */
export function stopConfettiSound(): void {
  if (!confettiAudio) return;
  try {
    confettiAudio.pause();
    confettiAudio.currentTime = 0;
  } catch {
    /* ignore */
  }
  confettiAudio = null;
}

/**
 * Soft two-tone chime via Web Audio (no asset file).
 * Used for generic notification alerts.
 */
export function playAlertChime(): void {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    void ctx.resume();
    const now = ctx.currentTime;
    playTone(ctx, 880, now, 0.12);
    playTone(ctx, 1175, now + 0.14, 0.16);
  } catch {
    /* ignore */
  }
}

/** Celebration sound — prefers confetti.mp3 (~30% volume), falls back to soft chime. */
export function playConfettiSound(): void {
  stopConfettiSound();
  const audio = playMp3(CONFETTI_SOUND_SRC, 0.3);
  if (audio) {
    confettiAudio = audio;
    audio.addEventListener(
      "ended",
      () => {
        if (confettiAudio === audio) confettiAudio = null;
      },
      { once: true },
    );
    return;
  }
  playAlertChime();
}

export function playOutbidFeedback(): void {
  alertVibrate([50, 80, 50, 80, 120]);
  if (!playMp3(OUTBID_SOUND_SRC, 0.85)) {
    playAlertChime();
  }
}

export function playWinFeedback(): void {
  alertVibrate([30, 40, 30, 40, 30, 40, 200]);
  playConfettiSound();
}
